import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, ArrowRight, CheckCircle2, Star, Clock, Users, Heart } from 'lucide-react';
import SEOHead, { generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../components/SEOHead';
import areaData from '../data/areaData';

export default function AreaLandingPage() {
    var params = useParams();
    var area = params.area;
    var data = areaData[area];

    if (!data) {
        return <Navigate to="/" replace />;
    }

    var pageTitle = 'Mantrailing & Dog Training Near ' + data.name + ' | Craven Canines';
    var pageDescription = 'Professional mantrailing sessions near ' + data.name + ', ' + data.county + '. Expert-led scent work for all dog breeds. Only ' + data.distance + ' from ' + data.name + '. Book today!';
    var pageKeywords = 'mantrailing ' + data.name + ', dog training ' + data.name + ', scent work ' + data.county;

    var schemas = [
        generateLocalBusinessSchema(),
        generateServiceSchema({
            name: 'Mantrailing Sessions Near ' + data.name,
            description: pageDescription,
            price: '30.00'
        }),
        generateBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Mantrailing', url: '/mantrailing' },
            { name: 'Near ' + data.name, url: '/mantrailing-near/' + area }
        ])
    ];

    var nearbyLinks = [];
    for (var i = 0; i < data.nearby.length; i++) {
        var town = data.nearby[i];
        var slug = town.toLowerCase().replace(/\s+/g, '-');
        if (areaData[slug]) {
            nearbyLinks.push({ town: town, slug: slug, hasPage: true });
        } else {
            nearbyLinks.push({ town: town, slug: slug, hasPage: false });
        }
    }

    return (
        <div className="min-h-screen" data-testid="area-landing-page">
            <SEOHead
                title={pageTitle}
                description={pageDescription}
                keywords={pageKeywords}
                canonical={'/mantrailing-near/' + area}
                schema={schemas}
            />

            {/* Hero */}
            <section className="relative py-28 md:py-40">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/5nx195fv_DSCF7164.JPG')" }}
                >
                    <div className="absolute inset-0 bg-primary/80"></div>
                </div>
                <div className="container-custom relative z-10">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-2 mb-4">
                            <MapPin className="w-5 h-5 text-white" />
                            <span className="text-white/90 font-medium">{data.distance} from {data.name}</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight font-serif">
                            Mantrailing & Dog Training Near {data.name}
                        </h1>
                        <p className="text-xl text-white/90 leading-relaxed mb-8">{data.description}</p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/mantrailing#booking-section">
                                <Button className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 h-auto rounded-full font-semibold shadow-lg" data-testid="area-cta-book">
                                    Book Your Session <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/contact">
                                <Button className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto rounded-full font-semibold" data-testid="area-cta-contact">
                                    Get in Touch
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="section-spacing" data-testid="area-why-section">
                <div className="container-custom">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif mb-4">
                            Why {data.name} Dog Owners Choose Craven Canines
                        </h2>
                        <p className="text-muted-foreground">Professional, positive, and passionate about helping every dog and handler succeed.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        <Card className="text-center">
                            <CardContent className="p-8">
                                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MapPin className="w-7 h-7 text-primary" />
                                </div>
                                <h3 className="text-lg font-bold mb-2 font-serif">Close to {data.name}</h3>
                                <p className="text-muted-foreground text-sm">Only {data.distance} drive. Sessions in beautiful countryside near Malmesbury.</p>
                            </CardContent>
                        </Card>
                        <Card className="text-center">
                            <CardContent className="p-8">
                                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Heart className="w-7 h-7 text-primary" />
                                </div>
                                <h3 className="text-lg font-bold mb-2 font-serif">All Dogs Welcome</h3>
                                <p className="text-muted-foreground text-sm">Puppies to seniors, nervous to energetic — every dog can enjoy mantrailing.</p>
                            </CardContent>
                        </Card>
                        <Card className="text-center">
                            <CardContent className="p-8">
                                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Star className="w-7 h-7 text-primary" />
                                </div>
                                <h3 className="text-lg font-bold mb-2 font-serif">Qualified Trainer</h3>
                                <p className="text-muted-foreground text-sm">Scentwork UK Tier 1 qualified. Level 5 Advanced Diploma in Applied Animal Behaviour.</p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Sessions */}
            <section className="section-spacing bg-secondary/30" data-testid="area-sessions">
                <div className="container-custom">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif mb-4">
                            Sessions Available Near {data.name}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <Card className="overflow-hidden">
                            <CardContent className="p-0">
                                <div className="bg-primary text-white p-6">
                                    <h3 className="text-xl font-bold font-serif">Introduction Workshop</h3>
                                    <div className="flex items-center gap-4 mt-2">
                                        <span className="flex items-center gap-1 text-white/80 text-sm"><Clock className="w-4 h-4" /> 3 hours</span>
                                        <span className="flex items-center gap-1 text-white/80 text-sm"><Users className="w-4 h-4" /> Small groups</span>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-3xl font-bold text-primary mb-3">£65</p>
                                    <p className="text-muted-foreground mb-4">The perfect starting point for {data.name} dog owners new to mantrailing.</p>
                                    <div className="space-y-2 mb-6">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                                            <span>No experience required</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                                            <span>All breeds and ages welcome</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                                            <span>Locations within 20 min of Malmesbury</span>
                                        </div>
                                    </div>
                                    <Link to="/mantrailing#booking-section">
                                        <Button className="w-full btn-primary">Book Introduction Workshop <ArrowRight className="w-4 h-4 ml-2" /></Button>
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <CardContent className="p-0">
                                <div className="bg-foreground text-white p-6">
                                    <h3 className="text-xl font-bold font-serif">Training Sessions</h3>
                                    <div className="flex items-center gap-4 mt-2">
                                        <span className="flex items-center gap-1 text-white/80 text-sm"><Clock className="w-4 h-4" /> 3 hours</span>
                                        <span className="flex items-center gap-1 text-white/80 text-sm"><Users className="w-4 h-4" /> Small groups</span>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-3xl font-bold text-primary mb-3">£30</p>
                                    <p className="text-muted-foreground mb-4">Progression sessions for handlers who have completed an intro workshop.</p>
                                    <div className="space-y-2 mb-6">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                                            <span>Progress at your own pace</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                                            <span>Various Wiltshire locations</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                                            <span>Supportive, positive methods</span>
                                        </div>
                                    </div>
                                    <Link to="/mantrailing#booking-section">
                                        <Button className="w-full btn-primary">Book Training Session <ArrowRight className="w-4 h-4 ml-2" /></Button>
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Nearby Areas */}
            <section className="section-spacing" data-testid="area-nearby">
                <div className="container-custom">
                    <div className="text-center max-w-2xl mx-auto mb-8">
                        <h2 className="text-2xl font-bold text-foreground font-serif mb-2">Also Serving Nearby Areas</h2>
                        <p className="text-muted-foreground">We welcome dog owners from across North Wiltshire and beyond.</p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto">
                        {nearbyLinks.map(function(item) {
                            if (item.hasPage) {
                                return (
                                    <Link key={item.town} to={'/mantrailing-near/' + item.slug} className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium hover:bg-primary/20 transition-colors">
                                        Mantrailing near {item.town}
                                    </Link>
                                );
                            }
                            return (
                                <span key={item.town} className="px-4 py-2 bg-secondary text-muted-foreground rounded-full text-sm font-medium">{item.town}</span>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-spacing bg-primary text-white" data-testid="area-final-cta">
                <div className="container-custom text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 font-serif">
                        Ready to Try Mantrailing Near {data.name}?
                    </h2>
                    <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                        Book your session today and discover why dogs and their owners across {data.county} love mantrailing.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/mantrailing#booking-section">
                            <Button className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 h-auto rounded-full font-semibold shadow-lg">
                                Book Now <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/faq">
                            <Button className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto rounded-full font-semibold">
                                Read FAQs
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
